import React, { useState, useEffect } from "react";
import Header from "./Header";
import HeroSection from "./HeroSection";
import SpecialtiesSection from "./SpecialtiesSection";
import StatsSection from "./StatsSection";
import HospitalsSection from "./HospitalsSection";
import Footer from "./Footer";
import BookAppointmentModal from "./BookAppointmentModal";
import SEO from "./SEO";
import { Card, CardContent } from "./ui/card";
import { StethoscopeIcon, TestTubeIcon, HandshakeIcon } from "lucide-react";

const Home = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Auto-open modal on first visit
  useEffect(() => {
    const hasVisitedBefore = localStorage.getItem('hasVisitedTheLivCure');
    
    if (!hasVisitedBefore) {
      // Small delay to ensure page is fully loaded
      const timer = setTimeout(() => {
        setIsModalOpen(true);
        localStorage.setItem('hasVisitedTheLivCure', 'true');
      }, 2000); // 2 second delay
      
      return () => clearTimeout(timer);
    }
  }, []);

  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "TheLivCure",
    "description": "India's leading surgical care platform providing advanced, minimally invasive treatments with personalized patient care and world-class medical expertise.",
    "url": "https://thelivcure.com",
    "telephone": ["+919311720045", "+919311720046"],
    "email": "tlc@thelivcure.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "G-627, Sangam Vihar",
      "addressLocality": "New Delhi",
      "postalCode": "110062",
      "addressCountry": "IN"
    },
    "areaServed": [
      { "@type": "City", "name": "Delhi" },
      { "@type": "City", "name": "Gurgaon" },
      { "@type": "City", "name": "Noida" },
      { "@type": "City", "name": "Faridabad" }
    ],
    "medicalSpecialty": [
      "Ophthalmology",
      "General Surgery",
      "Minimally Invasive Surgery"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Medical Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "MedicalProcedure",
            "name": "General Physician Consultations",
            "description": "Expert consultations for routine health issues, preventive care, and management of acute and chronic illnesses"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "MedicalProcedure",
            "name": "Diagnostic Services",
            "description": "Comprehensive lab tests and screenings with quick, reliable results"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "MedicalProcedure",
            "name": "Patient Support & Care Coordination",
            "description": "End-to-end assistance throughout the treatment journey"
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="TheLivCure – Advanced Surgical Care"
        description="TheLivCure - India's leading surgical care platform providing advanced, minimally invasive treatments with personalized patient care and world-class medical expertise. Expert doctors for ophthalmology, general surgery, and specialized treatments."
        keywords="medical treatment, surgical care, ophthalmology, eye surgery, LASIK, cataract surgery, hernia treatment, piles treatment, gallstone surgery, kidney stone treatment, circumcision, medical consultation, expert doctors, Delhi, Gurgaon, Noida, Faridabad"
        structuredData={homeStructuredData}
      />
      <Header />
      <HeroSection />

      {/* Key Services */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">
              Key Services — <span className="text-blue-600">TheLivcure</span>
            </h2>
            <p className="mt-3 text-sm sm:text-base text-gray-600 max-w-3xl mx-auto">
              At TheLivcure, we offer a comprehensive range of healthcare services with a focus on
              clinical excellence, personalized care, and seamless patient experiences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            <Card className="hover:shadow-lg transition-shadow border-2 border-blue-50">
              <CardContent className="p-5 sm:p-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                  <StethoscopeIcon className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">General Physician Consultations</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Expert consultations for routine health issues, preventive care, and management of acute and
                  chronic illnesses, ensuring accurate diagnosis and holistic treatment plans.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow border-2 border-purple-50">
              <CardContent className="p-5 sm:p-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                  <TestTubeIcon className="w-6 h-6 sm:w-7 sm:h-7 text-purple-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Diagnostic Services</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Comprehensive lab tests and screenings with quick, reliable results — including blood tests,
                  imaging referrals, and preventive health check-up packages.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow border-2 border-green-50">
              <CardContent className="p-5 sm:p-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <HandshakeIcon className="w-6 h-6 sm:w-7 sm:h-7 text-green-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Patient Support & Care Coordination</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  End-to-end assistance throughout the treatment journey — scheduling, diagnostics coordination,
                  insurance support, and structured follow-up for a hassle-free experience.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      <SpecialtiesSection />
      <StatsSection />
      {/* <HospitalsSection /> */}
      <Footer />
      
      {/* Auto-open Booking Modal */}
      <BookAppointmentModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        treatmentType="general-consultation"
        doctorName=""
      />
    </div>
  );
};

export default Home;